import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  ForwardedRef,
} from 'react';
import { useMutation } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  useTemplateConverter,
  TEMPLATE_CONVERSION_SOURCES,
  TEMPLATE_CONVERSION_TYPES,
  TemplateConversionSource,
  TemplateConversionType,
} from '~/src/hooks/useTemplateConverter';
import { useCurrentOrgFprint } from '~/src/entities/user/hooks/useCurrentOrg';
import { useIsClioAuthenticated } from '~/src/hooks/useClio';
import ClioLogo from '@clio/design-system-web-components/react/logo';
import { ClioDocumentPicker } from '~/src/components/ClioDocumentPicker';
import Button from '~/src/components/Button/Button';
import ConversionStatusCard from '~/src/components/DraftingSpecialist/ConversionStatusCard';
import ReviewTemplatesCard from '~/src/components/DraftingSpecialist/ReviewTemplatesCard';
import type { FileItem } from '~/src/hooks/useTemplateConverter';
import { ReactComponent as UploadIcon } from '~/src/static/upload-icon-2.svg';
import analyticsService from '~/src/services/analytics';
import {
  SelectableItem,
  isClioDocument,
  ITEM_SOURCES,
} from '../types/clioDocumentPicker';
import clioDocumentPickerService from '~/src/services/api/clioDocumentPickerService';
import { http } from '~/src/services/requests';
import { APIError } from '~/src/utils/error';

const useOrderedCardScroll = (refs: React.RefObject<HTMLDivElement>[]) => {
  const scrollElement = useCallback((element: HTMLDivElement) => {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Adjust scroll position to show 100px from top
    setTimeout(() => {
      window.scrollBy({
        top: 0,
        behavior: 'smooth',
      });
    }, 100);
  }, []);

  const scrollToCard = useCallback(
    (scrollTo: number) => {
      const ref = refs[scrollTo];

      if (ref && ref.current) {
        scrollElement(ref.current);
      }
    },
    [scrollElement, refs],
  );

  return {
    scrollToCard,
  };
};

const TrashIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const FileListItem = ({
  file: fileItem,
  onRemoveFile,
  isDisabled,
}: {
  file: FileItem;
  onRemoveFile: () => void;
  isDisabled: boolean;
}) => {
  const name = fileItem.file.name;
  const error = fileItem.error;

  return (
    <div
      className={`flex justify-between items-center text-gray-800 border rounded-lg px-4 py-2 ${
        error ? 'border-red-200 bg-red-50' : 'border-gray-200'
      }`}
    >
      <div className="flex grow items-center justify-between gap-2">
        <span className="flex-1 text-sm">{name}</span>
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>
      {!isDisabled && (
        <button
          onClick={onRemoveFile}
          className="ml-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
          aria-label={`Remove ${name}`}
        >
          <TrashIcon />
        </button>
      )}
    </div>
  );
};

const TemplateSetupFilesCard = forwardRef(
  (
    {
      files,
      onRemoveFile,
      onConvertClick,
      isConverting,
      isConversionDisabled,
      isUploadDisabled,
      jobFprint,
    }: {
      files: FileItem[];
      onRemoveFile: (index: number) => void;
      onConvertClick: () => void;
      isConverting: boolean;
      isConversionDisabled: boolean;
      isUploadDisabled: boolean;
      jobFprint: string | null;
    },
    ref: ForwardedRef<HTMLDivElement> | null,
  ) => {
    const handleRemoveFile = (index: number) => {
      onRemoveFile(index);
    };

    const handleConvertClick = () => {
      // Track analytics before converting
      const documentNames = files.map((file) => file.file.name);
      analyticsService.track(
        'Drafting Specialist - Clicked on Convert to Templates',
        {
          num_documents: files.length,
          documents_names: documentNames,
        },
      );

      onConvertClick();
    };

    return (
      <div className="mt-4" ref={ref}>
        <div className="bg-white border border-gray-200 rounded-xl p-8 max-w-2xl mx-auto flex flex-col gap-8">
          <div>
            <h2 className="text-base font-semibold text-gray-900 m-0 flex items-center gap-2">
              Review uploads
            </h2>
          </div>
          <div className="text-sm">
            Here are the documents you have uploaded. Once you are ready, they
            can be created into reusable templates.
          </div>
          <div className="space-y-2">
            {files.map((file, index) => (
              <FileListItem
                key={`${file.file.name}-${index}`}
                file={file}
                onRemoveFile={handleRemoveFile.bind(null, index)}
                isDisabled={isUploadDisabled}
              />
            ))}
          </div>
          {!jobFprint && (
            <div className="flex justify-end">
              <Button
                primary
                className="h-8 text-xs font-medium"
                onClick={handleConvertClick}
                disabled={isConversionDisabled}
                tabIndex={0}
                aria-label="Create templates now"
                data-pendo-id="Draft-AI-Create-template"
              >
                {isConverting ? 'Creating...' : 'Create templates'}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  },
);

TemplateSetupFilesCard.displayName = 'TemplateSetupFilesCard';

const FileUploadArea = forwardRef(
  (
    {
      isDisabled,
      isDragOver,
      selectedFiles,
      openFilePicker,
      handleDragOver,
      handleDragEnter,
      handleDragLeave,
      handleDrop,
      handleFileUpload,
    }: {
      isDisabled: boolean;
      isDragOver: boolean;
      selectedFiles: FileItem[];
      openFilePicker: () => void;
      handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
      handleDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
      handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
      handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
      handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    },
    ref: ForwardedRef<HTMLInputElement> | null,
  ) => {
    const handleUploadClick = () => {
      // Track analytics for upload documents click
      analyticsService.track(
        'Drafting Specialist - Clicked on Upload Documents',
      );

      // Call the original file picker function
      openFilePicker();
    };

    return (
      <div
        className={`flex-1 border-2 border-dashed rounded-lg p-4 py-8 text-center transition-colors group ${
          !isDisabled
            ? 'hover:cursor-pointer hover:border-blue-400 hover:bg-blue-50'
            : ''
        } ${
          isDragOver && !isDisabled
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 bg-gray-50/50 hover:bg-gray-50'
        } ${isDisabled ? 'cursor-not-allowed' : ''}`}
        onClick={isDisabled ? undefined : handleUploadClick}
        onDragOver={isDisabled ? undefined : handleDragOver}
        onDragEnter={isDisabled ? undefined : handleDragEnter}
        onDragLeave={isDisabled ? undefined : handleDragLeave}
        onDrop={isDisabled ? undefined : handleDrop}
      >
        <input
          ref={ref}
          type="file"
          accept=".docx"
          multiple
          onChange={handleFileUpload}
          className="hidden"
          aria-label="Upload .docx files"
        />

        <div className="flex flex-col items-center space-y-4">
          {/* Upload Icon */}
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
              !isDisabled ? 'group-hover:bg-blue-200' : ''
            } ${isDragOver && !isDisabled ? 'bg-blue-200' : 'bg-gray-200'}`}
          >
            <UploadIcon />
          </div>

          {/* Upload Text */}
          <div>
            <p
              className={`text-blue-600 font-medium text-lg ${
                !isDisabled ? 'text-blue-600' : ''
              }`}
            >
              {selectedFiles.length > 0
                ? `Selected: ${selectedFiles.length} file${
                    selectedFiles.length > 1 ? 's' : ''
                  }`
                : 'Upload document'}
            </p>
            <p
              className={`text-lg transition-colors ${
                isDragOver ? 'text-blue-600' : 'text-gray-500'
              }`}
            >
              {isDragOver ? 'Drop files here' : 'Or drag here to upload'}
            </p>
          </div>

          {/* File Info */}
          <div className="text-gray-500 text-sm space-y-1">
            <p>File supported: .DOCX</p>
            <p>Number of documents: 5</p>
            <p>Size limit: 5MB</p>
          </div>
        </div>
      </div>
    );
  },
);

FileUploadArea.displayName = 'FileUploadArea';

const ClioDocumentPickerArea = ({
  onClick,
  isDisabled,
}: {
  onClick: () => void;
  isDisabled: boolean;
}) => (
  <div className="flex-1 p-4 py-8 mr-4 flex-grow border-2 border-solid rounded-lg transition-colors hover:bg-blue-50 hover:border-blue-400 hover:custor-pointer">
    <button
      onClick={onClick}
      className="w-full h-full px-4 py-2"
      disabled={isDisabled}
    >
      <div className="mb-2 w-16 h-16 mx-auto flex items-center justify-center">
        <ClioLogo product="gem" className="h-10" />
      </div>
      <span className="font-medium text-lg text-blue-600">
        Select documents and templates from Clio Manage
      </span>
      <div className="text-gray-500 text-sm mt-6">
        <p>File supported: .DOCX</p>
        <p>Number of documents: 5</p>
        <p>Size limit: 5MB</p>
      </div>
    </button>
  </div>
);

const TemplateSetupContainer = () => {
  const snackbar = useSnackbar();
  const {
    selectedFiles,
    isDragOver,
    fileInputRef,
    jobFprint,
    jobStatus,
    jobData,
    handleReset,
    handleFileUpload,
    handleConvertClick,
    openFilePicker,
    handleRemoveFile,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    addFiles,
  } = useTemplateConverter();

  // Helper function to convert blob to File and add to converter
  const addFileFromBlob = useCallback(
    (
      blob: Blob,
      fileName: string,
      source: TemplateConversionSource = TEMPLATE_CONVERSION_SOURCES.DRAFT,
      type: TemplateConversionType = TEMPLATE_CONVERSION_TYPES.DOCUMENT,
    ) => {
      // Create a File object from the blob
      const file = new File([blob], fileName, {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        lastModified: Date.now(),
      });

      // Create a mock FileList and add files with the correct source and type
      const dt = new DataTransfer();
      dt.items.add(file);
      const fileList = dt.files;

      // Directly add files with the specified source and type
      addFiles(fileList, source, type);
    },
    [addFiles],
  );

  const [showPicker, setShowPicker] = useState(false);

  const currentOrgFprint = useCurrentOrgFprint();
  const isClioAuthenticated = useIsClioAuthenticated();

  const uploadCardRef = useRef<HTMLDivElement>(null);
  const filesCardRef = useRef<HTMLDivElement>(null);
  const conversionCardRef = useRef<HTMLDivElement>(null);
  const reviewCardRef = useRef<HTMLDivElement>(null);

  const cardToScrollTo = useMemo(() => {
    if (jobStatus === 'completed' && jobFprint) {
      return 3;
    }

    if ((jobStatus === 'pending' || jobStatus === 'processing') && jobFprint) {
      return 2;
    }

    if (selectedFiles.length > 0) {
      return 1;
    }

    return 0;
  }, [selectedFiles.length, jobStatus, jobFprint]);

  const { scrollToCard } = useOrderedCardScroll([
    uploadCardRef,
    filesCardRef,
    conversionCardRef,
    reviewCardRef,
  ]);

  useEffect(() => {
    if (cardToScrollTo) {
      scrollToCard(cardToScrollTo);
    }
  }, [cardToScrollTo, scrollToCard]);

  const isTemplateUploadDisabled = useMemo(() => {
    return (
      jobStatus === 'completed' ||
      jobStatus === 'failed' ||
      jobStatus === 'pending' ||
      jobStatus === 'processing'
    );
  }, [jobStatus]);

  const isTemplateConversionInProgress = useMemo(() => {
    return jobStatus === 'pending' || jobStatus === 'processing';
  }, [jobStatus]);

  const isTemplateConversionDisabled = useMemo(() => {
    const fileErrors = selectedFiles.some(
      (item) => typeof item.error === 'string',
    );

    return (
      isTemplateUploadDisabled || fileErrors || isTemplateConversionInProgress
    );
  }, [isTemplateUploadDisabled, selectedFiles, isTemplateConversionInProgress]);

  const handleResetClick = () => {
    handleReset();
    scrollToCard(0);
  };

  const cancelJobMutation = useMutation<void, APIError, void>({
    mutationFn: async () => {
      if (!currentOrgFprint || !jobFprint) {
        throw new Error('Missing orgFprint or jobFprint');
      }
      return await http.innerSendAuthenticated(
        {
          relativePath: `/document/api/v1/org/${currentOrgFprint}/template/convert/job/${jobFprint}/cancel/`,
          method: 'PATCH',
        },
        true,
      );
    },
    onSuccess: () => {
      handleReset();
      scrollToCard(0);
    },
    onError: (error: APIError) => {
      snackbar.enqueueSnackbar(
        `Failed to cancel creation. Please contact the administrator. ${error.message}`,
        {
          variant: 'error',
          autoHideDuration: 5000,
        },
      );
    },
  });

  const handleCancelClick = () => {
    cancelJobMutation.mutate();
  };

  const handleClioPickerClick = () => {
    analyticsService.track(
      'Drafting Specialist - Clicked on Select from Manage',
    );
    setShowPicker(true);
  };

  const handleDocumentPickerSelect = useCallback(
    async (items: SelectableItem[]) => {
      // Ensure we have an array to work with
      if (!items || items.length === 0) return;

      try {
        // Process all items sequentially
        for (const item of items) {
          const filename = item.filename;

          // Validate item has a known source type
          if (
            !item.source ||
            (item.source !== ITEM_SOURCES.DOCUMENT &&
              item.source !== ITEM_SOURCES.TEMPLATE)
          ) {
            console.error('Unknown item type:', item);
            continue;
          }

          try {
            // Download item using appropriate endpoint for documents vs templates
            const response =
              item.source === ITEM_SOURCES.TEMPLATE
                ? await clioDocumentPickerService.downloadDocumentTemplate(
                    currentOrgFprint,
                    item.id,
                  )
                : await clioDocumentPickerService.downloadDocument(
                    currentOrgFprint,
                    item.id,
                  );

            // Convert response to blob
            const blob = await response.blob();

            // Add the downloaded file to the template converter with appropriate source and type
            const source = TEMPLATE_CONVERSION_SOURCES.MANAGE; // All items from Clio are from 'manage'
            const type =
              item.source === ITEM_SOURCES.TEMPLATE
                ? TEMPLATE_CONVERSION_TYPES.TEMPLATE
                : TEMPLATE_CONVERSION_TYPES.DOCUMENT;
            addFileFromBlob(blob, filename, source, type);

            // Analytics tracking for each item - capitalize for display
            const displayType =
              item.source.charAt(0).toUpperCase() + item.source.slice(1);
            analyticsService.track(`Clio ${displayType} Selected`, {
              itemId: item.id,
              itemName: filename,
              fileSize: item.size || 0,
              matterId: isClioDocument(item) ? item.matter?.id || null : null,
              integration: 'clio',
            });
          } catch (itemError: any) {
            console.error(
              `Failed to download ${item.source} ${filename}:`,
              itemError,
            );
            // Show error for individual item but continue with others
            alert(
              `Failed to download ${item.source} "${filename}": ${
                itemError.message || 'Unknown error'
              }`,
            );
          }
        }
      } catch (error: any) {
        throw new Error(
          `Failed to download documents: ${error.message || 'Unknown error'}`,
        );
      }

      // Note: The picker now handles closing itself after downloads complete
    },
    [currentOrgFprint, addFileFromBlob],
  );

  return (
    <div className="min-h-full overflow-y-auto">
      <div ref={uploadCardRef}>
        <div className="bg-white border border-gray-200 rounded-xl p-8 mt-4 max-w-2xl mx-auto flex flex-col gap-8">
          <div>
            <h2 className="text-base font-semibold text-gray-900 m-0 flex items-center gap-2">
              Select documents and templates
            </h2>
          </div>
          <div className="text-sm">
            To get started, select documents or templates from Clio Manage or
            upload a document.
          </div>
          <div className="flex h-full">
            {isClioAuthenticated && (
              <>
                <ClioDocumentPickerArea
                  onClick={handleClioPickerClick}
                  isDisabled={isTemplateUploadDisabled}
                />
                <ClioDocumentPicker
                  isOpen={showPicker}
                  onClose={() => setShowPicker(false)}
                  onSelect={handleDocumentPickerSelect}
                  orgId={currentOrgFprint}
                  maxSelectionLimit={5}
                />
              </>
            )}
            <FileUploadArea
              ref={fileInputRef}
              isDisabled={isTemplateUploadDisabled}
              isDragOver={isDragOver}
              selectedFiles={selectedFiles}
              openFilePicker={openFilePicker}
              handleDragOver={handleDragOver}
              handleDragEnter={handleDragEnter}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              handleFileUpload={handleFileUpload}
            />
          </div>
        </div>
      </div>
      {selectedFiles.length > 0 && (
        <TemplateSetupFilesCard
          ref={filesCardRef}
          files={selectedFiles}
          onRemoveFile={handleRemoveFile}
          onConvertClick={handleConvertClick}
          isConverting={isTemplateConversionInProgress}
          isUploadDisabled={isTemplateUploadDisabled}
          isConversionDisabled={isTemplateConversionDisabled}
          jobFprint={jobFprint}
        />
      )}
      {jobFprint && (
        <div className="mt-4" ref={conversionCardRef}>
          <ConversionStatusCard
            jobData={jobData}
            onSelectMoreDocuments={handleResetClick}
            onCancelCreation={handleCancelClick}
            isCancelling={cancelJobMutation.isLoading}
          />
        </div>
      )}
      {jobFprint && (
        <div className="mt-4" ref={reviewCardRef}>
          <ReviewTemplatesCard
            jobData={jobData}
            onSelectMoreDocuments={handleResetClick}
          />
        </div>
      )}
    </div>
  );
};

export default TemplateSetupContainer;
